import { useCallback, type RefObject } from 'react';
import { RefreshCw } from 'lucide-react';
import { Spinner } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { PULL_TRIGGER_DISTANCE, pullProgress } from '../../lib/pull-to-refresh';

/** Altura do círculo + folga, para ele começar escondido atrás da topbar. */
const HIDDEN_OFFSET = 46;

interface PullToRefreshProps {
  /** Scroller principal do layout; o gesto só vale quando ele está no topo. */
  scrollRef: RefObject<HTMLElement | null>;
}

/**
 * Arrastar para baixo no topo recarrega os dados da tela. O indicador fica no
 * topo da área de scroll (abaixo da topbar sticky) e sobe por trás dela.
 */
export function PullToRefresh({ scrollRef }: PullToRefreshProps) {
  const queryClient = useQueryClient();
  const onRefresh = useCallback(
    () => queryClient.refetchQueries({ type: 'active' }),
    [queryClient],
  );
  const { distance, isRefreshing } = usePullToRefresh({ scrollRef, onRefresh });

  const dragging = distance > 0 && !isRefreshing;
  const offset = (isRefreshing ? PULL_TRIGGER_DISTANCE : distance) - HIDDEN_OFFSET;
  const progress = pullProgress(distance);

  // A faixa é alta o bastante para o círculo descer inteiro; o overflow só
  // esconde ele em repouso, acima da faixa e atrás da topbar.
  return (
    <div
      aria-live="polite"
      className="pointer-events-none absolute inset-x-0 top-0 z-20 flex h-32 items-start justify-center overflow-hidden"
    >
      <div
        className="flex size-9 items-center justify-center rounded-full border border-separator bg-surface text-muted shadow-md"
        style={{
          transform: `translateY(${offset}px)`,
          transition: dragging ? 'none' : 'transform 220ms ease-out',
        }}
      >
        {isRefreshing ? (
          <>
            <Spinner size="sm" color="current" />
            <span className="sr-only">Atualizando</span>
          </>
        ) : (
          <RefreshCw
            size={16}
            style={{ transform: `rotate(${progress * 270}deg)` }}
            className={progress >= 1 ? 'text-accent' : undefined}
          />
        )}
      </div>
    </div>
  );
}
